/**
 * Hook for Web Search Usage SSE Notifications
 *
 * Listens to SSE events for web search limit warnings and shows toast notifications.
 */

import { useEffect, useRef, useCallback } from 'react';
import { showWarningNotification, showErrorNotification } from '../services/notificationService';
import { useAuth } from './useAuth';
import { logger } from '@/utils/logger';

interface WebSearchUsageData {
  agentId: string;
  agentName: string;
  clientId: number;
  usage: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  timestamp: string;
}

interface UseWebSearchNotificationsOptions {
  enabled?: boolean;
  sseEndpoint?: string;
}

export function useWebSearchNotifications(options: UseWebSearchNotificationsOptions = {}) {
  const { enabled = true, sseEndpoint = '/api/langchain/sse/handover' } = options;
  const { user, isAuthenticated } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stabilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const handleWarning = useCallback((data: WebSearchUsageData) => {
    showWarningNotification(
      `Web Search: Uso Elevato`,
      `L'agent "${data.agentName}" ha raggiunto il ${data.percentUsed.toFixed(0)}% del limite mensile (${data.usage}/${data.limit} ricerche). Rimanenti: ${data.remaining}`,
      { autoClose: 10000 }
    );
  }, []);

  const handleLimitReached = useCallback((data: WebSearchUsageData) => {
    showErrorNotification(
      `Web Search: Limite Raggiunto`,
      `L'agent "${data.agentName}" ha superato il limite mensile di ${data.limit} ricerche. Web search disabilitato fino al prossimo reset.`,
      { autoClose: false }
    );
  }, []);

  const connect = useCallback(() => {
    // Don't connect if not authenticated or not enabled
    if (!isAuthenticated || !user || !enabled) return;

    // Don't reconnect if we've exceeded max attempts
    if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
      logger.warn('[WebSearchNotifications] Max reconnect attempts reached');
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Cancel any pending stability timer from a previous connection
    if (stabilityTimeoutRef.current) {
      clearTimeout(stabilityTimeoutRef.current);
      stabilityTimeoutRef.current = null;
    }

    try {
      const eventSource = new EventSource(sseEndpoint, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        logger.log('[WebSearchNotifications] SSE connected');
        // Only reset the counter after the connection has stayed stable for 10 seconds.
        // This prevents ephemeral connections (open + immediate error) from resetting
        // the counter and bypassing exponential backoff.
        stabilityTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current = 0;
          stabilityTimeoutRef.current = null;
        }, 10000);
      };

      eventSource.onerror = (error) => {
        logger.warn('[WebSearchNotifications] SSE error:', error);

        // Cancel the stability timer — the connection did not stay up long enough
        if (stabilityTimeoutRef.current) {
          clearTimeout(stabilityTimeoutRef.current);
          stabilityTimeoutRef.current = null;
        }

        eventSource.close();
        eventSourceRef.current = null;

        // Attempt reconnection with exponential backoff
        reconnectAttempts.current++;
        const backoffTime = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffTime);
      };

      // Listen for web search warning events
      eventSource.addEventListener('web-search-limit-warning', (event) => {
        try {
          const data: WebSearchUsageData = JSON.parse(event.data);
          handleWarning(data);
        } catch (err) {
          logger.error('[WebSearchNotifications] Error parsing warning event:', err);
        }
      });

      // Listen for web search limit reached events
      eventSource.addEventListener('web-search-limit-reached', (event) => {
        try {
          const data: WebSearchUsageData = JSON.parse(event.data);
          handleLimitReached(data);
        } catch (err) {
          logger.error('[WebSearchNotifications] Error parsing limit reached event:', err);
        }
      });

    } catch (err) {
      logger.error('[WebSearchNotifications] Error creating EventSource:', err);
    }
  }, [isAuthenticated, user, enabled, sseEndpoint, handleWarning, handleLimitReached]);

  // Connect on mount and when dependencies change
  useEffect(() => {
    connect();

    return () => {
      // Cleanup on unmount
      if (stabilityTimeoutRef.current) {
        clearTimeout(stabilityTimeoutRef.current);
        stabilityTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);

  // Expose manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  return {
    reconnect,
    isConnected: !!eventSourceRef.current,
  };
}

export default useWebSearchNotifications;
